import React from 'react';
import {
    Drawer,
    List,
    ListItemButton,
    ListItemText,
    Typography,
    Box,
    Divider,
    Avatar,
    ListItemIcon,
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../authentication/AuthContext';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ChatIcon from '@mui/icons-material/Chat';
import UploadIcon from '@mui/icons-material/Upload';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import PeopleIcon from '@mui/icons-material/People';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import DescriptionIcon from '@mui/icons-material/Description';
import ActivityIcon from '@mui/icons-material/Timeline';
import { useTheme } from '@mui/material/styles';
// Icon mapping for backend icons
const iconMap = {
    users: <PeopleIcon />,
    chat: <ChatIcon />,
    documents: <DescriptionIcon />,
    analytics: <AnalyticsIcon />,
    settings: <SettingsIcon />,
    activity: <ActivityIcon />,
    dashboard: <DashboardIcon />,
    upload: <UploadIcon />,
};

const SIDEBAR_WIDTH = 260;

export default function SideMenu() {
    const { user, membership, dashboardData, logout } = useAuth();
    const location = useLocation();
    const theme = useTheme();
    const isActive = (path) =>
        location.pathname === path ||
        (path !== '/dashboard' && location.pathname.startsWith(path));

    return (
        <Drawer
            variant="permanent"
            anchor="left"
            sx={{
                width: SIDEBAR_WIDTH,
                flexShrink: 0,
                display: { xs: 'none', md: 'block' },
                '& .MuiDrawer-paper': {
                    width: SIDEBAR_WIDTH,
                    boxSizing: 'border-box',
                    backgroundColor: theme.palette.background.paper,
                    borderRight: '1px solid',
                    borderColor: theme.palette.divider,
                    boxShadow: 'none',
                },
            }}
        >
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
                    {user?.first_name
                        ? user.first_name.charAt(0).toUpperCase()
                        : 'U'}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body1" fontWeight="medium" noWrap>
                        {user?.first_name} {user?.last_name}
                    </Typography>
                    {(membership?.company?.name ||
                        membership?.company_name) && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            display="block"
                            sx={{ mt: 0.5 }}
                        >
                            {membership?.company?.name ||
                                membership?.company_name}
                        </Typography>
                    )}
                </Box>
            </Box>

            <Divider />

            <List sx={{ flexGrow: 1, px: 1.5, py: 1 }}>
                {/* Dashboard link - always show */}
                <ListItemButton
                    key="dashboard"
                    component={RouterLink}
                    to="/dashboard"
                    selected={isActive('/dashboard')}
                    sx={{
                        borderRadius: 2,
                        mb: 0.5,
                        '&.Mui-selected': {
                            '&:hover': {
                                backgroundColor: theme.palette.primary.light,
                            },
                            '& .MuiListItemIcon-root': { color: 'inherit' },
                        },
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                        <DashboardIcon />
                    </ListItemIcon>
                    <ListItemText primary="Dashboard" />
                </ListItemButton>

                {/* Role-based menu items from backend */}
                {dashboardData?.menu_items?.map((menu_items) => (
                    <React.Fragment key={menu_items.name}>
                        <ListItemButton
                            component={RouterLink}
                            to={menu_items.endpoint}
                            selected={isActive(menu_items.endpoint)}
                            sx={{
                                borderRadius: 2,
                                mb: 0.5,
                                '&.Mui-selected': {
                                    '&:hover': {
                                        backgroundColor:
                                            theme.palette.primary.light,
                                    },
                                    '& .MuiListItemIcon-root': {
                                        color: 'inherit',
                                    },
                                },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                                {iconMap[menu_items.icon] || (
                                    <DescriptionIcon />
                                )}
                            </ListItemIcon>
                            <ListItemText primary={menu_items.name} />
                        </ListItemButton>
                        {menu_items.name === 'Preferences' && (
                            <Divider sx={{ my: 1.5 }} />
                        )}
                    </React.Fragment>
                ))}

                <Divider sx={{ my: 2 }} />

                <ListItemButton
                    component={RouterLink}
                    to="/"
                    key="logout"
                    onClick={logout}
                    sx={{ borderRadius: 2, mb: 0.5 }}
                >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                        <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText primary="Logout" />
                </ListItemButton>
            </List>

            <Box sx={{ mt: 'auto', p: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <img
                        src="/stacy_secure_ai_logo_bevel.png"
                        alt="STACY"
                        width="100px"
                    />
                </Box>
            </Box>
        </Drawer>
    );
}
